import React, { useState, useEffect } from 'react';
import { 
  LayoutDashboard, Users, CheckSquare, DollarSign, 
  BarChart3, Settings, Bell, Search, Menu, Plus, 
  Filter, MoreVertical, ArrowLeft, Clock, CheckCircle2, 
  AlertCircle, TrendingUp, Calendar, Briefcase, Mail, X, Trash2, Loader2, LogOut, Edit, Wallet, TrendingDown, Receipt,
  HeartHandshake, Star, Copy, Smile, Meh, Frown
} from 'lucide-react';

// ==========================================
// CONFIGURAÇÃO DO FIREBASE
// ==========================================
import { initializeApp } from 'firebase/app';
import { getAuth, signInWithEmailAndPassword, signOut, onAuthStateChanged, signInAnonymously } from 'firebase/auth';
import { getFirestore, collection, doc, setDoc, updateDoc, deleteDoc, onSnapshot } from 'firebase/firestore';

// Sua configuração do Firebase
const firebaseConfig = {
  apiKey: "AIzaSyCDWJSZ3feULs0LyGv5HWQKwYUSosxBJ9s",
  authDomain: "mind-assessoria.firebaseapp.com",
  projectId: "mind-assessoria",
  storageBucket: "mind-assessoria.firebasestorage.app",
  messagingSenderId: "958701727558",
  appId: "1:958701727558:web:f63a5fe5040d616b6ebf47",
  measurementId: "G-XD5CVVG093"
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const appId = 'mind-assessoria'; 

// ==========================================
// DADOS INICIAIS
// ==========================================
const INITIAL_USERS = [];
const INITIAL_CLIENTS = [];
const INITIAL_TASKS = [];
const INITIAL_SALES = [];
const INITIAL_EXPENSES = [];
const INITIAL_NPS = [];

// ==========================================
// COMPONENTES COMPARTILHADOS
// ==========================================
const Card = ({ children, className = '', ...props }) => (
  <div className={`bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden ${className}`} {...props}>
    {children}
  </div>
);

const Badge = ({ children, type = 'default', className = '' }) => {
  const styles = {
    default: 'bg-slate-100 text-slate-700', success: 'bg-emerald-100 text-emerald-700',
    warning: 'bg-amber-100 text-amber-700', danger: 'bg-rose-100 text-rose-700',
    brand: 'bg-[#ff4d00]/10 text-[#d63a00]', purple: 'bg-purple-100 text-purple-700',
    blue: 'bg-blue-100 text-blue-700'
  };
  return <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${styles[type] || styles.default} ${className}`}>{children}</span>;
};

const Avatar = ({ src, name, size = 'md' }) => {
  const sizes = { sm: 'w-8 h-8', md: 'w-10 h-10', lg: 'w-16 h-16' };
  return <img src={src} alt={name} className={`${sizes[size]} rounded-full border-2 border-white shadow-sm object-cover bg-slate-100`} title={name} />;
};

const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-fade-in">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
        <div className="flex justify-between items-center p-5 border-b border-slate-100">
          <h3 className="font-bold text-lg text-slate-800">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 transition-colors bg-slate-50 p-1.5 rounded-lg"><X size={20} /></button>
        </div>
        <div className="p-5 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
};

// ==========================================
// TELA DE LOGIN
// ==========================================
const LoginScreen = ({ onLogin, error, isLoading }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onLogin(email, password);
  };

  return (
    <div className="flex h-screen bg-[#0a0a0a] items-center justify-center font-sans p-4 relative overflow-hidden">
      <div className="absolute top-[-20%] left-[-10%] w-96 h-96 bg-[#ff4d00] opacity-20 rounded-full blur-[100px]"></div>
      <div className="absolute bottom-[-20%] right-[-10%] w-96 h-96 bg-[#ff4d00] opacity-10 rounded-full blur-[100px]"></div>

      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 sm:p-10 relative z-10 animate-fade-in">
        <div className="flex justify-center mb-6">
          <img 
            src="/logo.png" alt="Mind Assessoria" 
            className="w-16 h-16 rounded-xl shadow-lg shadow-[#ff4d00]/20 object-cover" 
            onError={(e) => { e.target.onerror = null; e.target.src = "https://ui-avatars.com/api/?name=M&background=ff4d00&color=fff&rounded=false&bold=true"; }} 
          />
        </div>
        <h2 className="text-2xl font-bold text-center text-slate-800 mb-2">Bem-vindo de volta</h2>
        <p className="text-center text-slate-500 text-sm mb-8">Faça login para acessar o painel da Mind Assessoria.</p>

        {error && (
          <div className="mb-6 p-3 bg-rose-50 border border-rose-100 rounded-lg text-rose-600 text-sm flex items-center gap-2 animate-fade-in">
            <AlertCircle size={16} className="flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">E-mail</label>
            <input type="email" required value={email} onChange={e=>setEmail(e.target.value)} className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:ring-2 focus:ring-[#ff4d00]/30 focus:border-[#ff4d00] outline-none transition-all text-sm" placeholder="seu@email.com" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Senha</label>
            <input type="password" required value={password} onChange={e=>setPassword(e.target.value)} className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:ring-2 focus:ring-[#ff4d00]/30 focus:border-[#ff4d00] outline-none transition-all text-sm" placeholder="••••••••" />
          </div>
          <button type="submit" disabled={isLoading} className="w-full bg-gradient-to-r from-[#ff4d00] to-[#d63a00] hover:from-[#d63a00] hover:to-[#b33000] text-white font-semibold py-3 rounded-lg transition-all shadow-md shadow-[#ff4d00]/20 flex justify-center items-center gap-2 mt-4 transform active:scale-[0.98]">
            {isLoading ? <Loader2 size={20} className="animate-spin" /> : 'Entrar no Sistema'}
          </button>
        </form>
      </div>
    </div>
  );
};

// ==========================================
// UTILITÁRIOS
// ==========================================
const formatTextWithLinks = (text) => {
  if (!text) return null;
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  return text.split(urlRegex).map((part, i) => {
    if (part.match(urlRegex)) {
      if (part.match(/\.(jpeg|jpg|gif|png|webp)$/i)) {
         return <img key={i} src={part} alt="Anexo" className="mt-2 mb-2 max-w-full rounded-lg border border-slate-200" onClick={(e) => e.stopPropagation()} />;
      }
      return <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="text-[#ff4d00] hover:underline break-all" onClick={(e) => e.stopPropagation()}>{part}</a>;
    }
    return part;
  });
};

const getDeadlineStyle = (deadline, status) => {
  if (status === 'concluida') return 'text-emerald-600 font-bold';
  
  const today = new Date();
  today.setHours(0, 0, 0, 0); 
  
  const [year, month, day] = deadline.split('-');
  const taskDate = new Date(year, month - 1, day);
  
  const diffDays = Math.ceil((taskDate - today) / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return 'text-rose-600 font-bold'; 
  if (diffDays <= 2) return 'text-amber-500 font-bold'; 
  return 'text-slate-500 font-medium'; 
};

// ==========================================
// TELA PÚBLICA DO FORMULÁRIO NPS
// ==========================================
const NpsPublicForm = ({ clientId, db, appId, auth }) => {
  const [score, setScore] = useState(null);
  const [comment, setComment] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (score === null) return;
    setIsSubmitting(true);
    try {
      await signInAnonymously(auth);
      const newNps = {
        id: `nps${Date.now()}`,
        clientId: clientId,
        score: score,
        comment: comment,
        date: new Date().toISOString()
      };
      await setDoc(doc(db, `artifacts/${appId}/public/data/nps`, newNps.id), newNps);
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      alert('Ocorreu um erro ao enviar seu feedback. Tente novamente.');
    }
    setIsSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-2xl shadow-xl text-center max-w-md w-full animate-fade-in border-t-4 border-emerald-500">
          <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 size={32} />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Muito Obrigado!</h2>
          <p className="text-slate-600">Seu feedback é fundamental para continuarmos melhorando nossos serviços.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 font-sans">
      <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-xl max-w-lg w-full animate-fade-in border-t-4 border-[#ff4d00]">
        <div className="flex justify-center mb-6">
          <img src="/logo.png" alt="Logo" className="w-16 h-16 rounded-xl shadow-md object-cover" onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }} />
        </div>
        <h2 className="text-xl font-bold text-center text-slate-800 mb-2">Pesquisa de Satisfação</h2>
        <p className="text-center text-slate-600 text-sm mb-8">Em uma escala de 0 a 10, o quanto você recomendaria a nossa Assessoria para um amigo ou colega?</p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="flex justify-between items-center gap-1 sm:gap-2">
            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
              <button
                key={num}
                type="button"
                onClick={() => setScore(num)}
                className={`w-8 h-10 sm:w-10 sm:h-12 rounded flex items-center justify-center font-bold transition-all ${
                  score === num 
                    ? (num <= 6 ? 'bg-rose-500 text-white' : num <= 8 ? 'bg-amber-500 text-white' : 'bg-emerald-500 text-white transform scale-110 shadow-md')
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {num}
              </button>
            ))}
          </div>
          <div className="flex justify-between text-xs text-slate-400 font-medium uppercase px-1">
            <span>0 - Nada Provável</span>
            <span>10 - Muito Provável</span>
          </div>

          <div className="pt-4 border-t border-slate-100">
            <label className="block text-sm font-medium text-slate-700 mb-2">O que motivou sua nota? (Opcional)</label>
            <textarea 
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows="4" 
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-[#ff4d00]/30 focus:border-[#ff4d00] outline-none transition-all text-sm resize-none" 
              placeholder="Deixe seu comentário, elogio ou sugestão de melhoria..."
            ></textarea>
          </div>

          <button 
            type="submit" 
            disabled={score === null || isSubmitting} 
            className={`w-full py-3 rounded-xl font-bold transition-all flex justify-center items-center gap-2 ${
              score !== null && !isSubmitting ? 'bg-[#ff4d00] hover:bg-[#d63a00] text-white shadow-md shadow-[#ff4d00]/20' : 'bg-slate-200 text-slate-400 cursor-not-allowed'
            }`}
          >
            {isSubmitting ? <Loader2 size={20} className="animate-spin" /> : 'Enviar Avaliação'}
          </button>
        </form>
      </div>
    </div>
  );
};

// ==========================================
// ABAS DE CONTEÚDO
// ==========================================
const DashboardTab = ({ setTab, clients, tasks, sales, expenses, goals, onOpenNewClient, onEditGoal }) => {
  const activeClients = clients.filter(c => c.status === 'ativo').length;
  const currentRevenue = clients.filter(c => c.status === 'ativo').reduce((acc, curr) => acc + curr.mrr, 0);
  
  const currentMonthStr = new Date().toISOString().slice(0, 7); 
  
  const salesThisMonth = sales
    .filter(s => s.type !== 'cancelamento' && s.date && s.date.startsWith(currentMonthStr))
    .reduce((acc, curr) => acc + curr.value, 0);

  const expensesThisMonth = expenses
    .filter(e => e.date && e.date.startsWith(currentMonthStr))
    .reduce((acc, curr) => acc + curr.value, 0);

  const totalRevenueThisMonth = currentRevenue + salesThisMonth;
  const netProfit = totalRevenueThisMonth - expensesThisMonth;
  const profitMargin = totalRevenueThisMonth > 0 ? ((netProfit / totalRevenueThisMonth) * 100).toFixed(1) : 0;

  const targetRevenue = goals.monthlyRevenue || 50000;
  const goalPercentage = Math.min((currentRevenue / targetRevenue) * 100, 100).toFixed(1);

  const chartData = React.useMemo(() => {
    const result = [];
    const now = new Date();
    const startYear = 2026;
    const startMonth = 3; 

    let displayStartYear = startYear;
    let displayStartMonth = startMonth;

    const monthsSinceStart = (now.getFullYear() - startYear) * 12 + (now.getMonth() - startMonth);

    if (monthsSinceStart >= 6) {
      displayStartYear = now.getFullYear();
      displayStartMonth = now.getMonth() - 5;
    }

    for (let i = 0; i < 6; i++) {
      const targetDate = new Date(displayStartYear, displayStartMonth + i, 1);
      const mYear = targetDate.getFullYear();
      const mMonth = targetDate.getMonth();
      const monthName = targetDate.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '');
      
      const isFuture = mYear > now.getFullYear() || (mYear === now.getFullYear() && mMonth > now.getMonth());

      let val = 0;
      if (!isFuture) {
          const endOfMonth = new Date(mYear, mMonth + 1, 0, 23, 59, 59);
          clients.forEach(c => {
              const startDate = new Date(c.startDate);
              if (startDate <= endOfMonth) {
                  if (c.status === 'ativo') {
                      val += c.mrr;
                  } else if (c.status === 'cancelado') {
                      const cancelSale = sales.find(s => s.clientId === c.id && s.type === 'cancelamento');
                      if (cancelSale) {
                          const cancelDate = new Date(cancelSale.date);
                          if (cancelDate > endOfMonth) {
                              val += c.mrr;
                          }
                      }
                  }
              }
          });
      }

      result.push({
        label: monthName.charAt(0).toUpperCase() + monthName.slice(1),
        val: val,
        isFuture: isFuture
      });
    }
    return result;
  }, [clients, sales]);

  const chartMax = Math.max(targetRevenue * 1.2, ...chartData.map(d => d.val).map(v => v * 1.2), 1000);
  const goalTopPercent = Math.max(0, 100 - ((targetRevenue / chartMax) * 100));

  const delayedTasks = tasks.filter(t => t.status === 'atrasada' || (t.status === 'pendente' && new Date(t.deadline) < new Date()));
  const completedTasks = tasks.filter(t => t.status === 'concluida').length;

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Visão Geral</h2>
          <p className="text-sm text-slate-500">Acompanhe os principais indicadores financeiros e operacionais.</p>
        </div>
        <button onClick={onOpenNewClient} className="bg-[#ff4d00] hover:bg-[#d63a00] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 shadow-sm">
          <Plus size={16} /> Novo Cliente
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-5 flex flex-col gap-3 border-l-4 border-l-[#ff4d00]">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm font-medium text-slate-500">Receita Recorrente</p>
              <h3 className="text-2xl font-bold text-slate-800">R$ {currentRevenue.toLocaleString('pt-BR')}</h3>
            </div>
            <div className="p-2 bg-[#ff4d00]/10 rounded-lg text-[#ff4d00]"><DollarSign size={20} /></div>
          </div>
          <div className="flex items-center gap-1 text-sm">
            <span className="text-emerald-500 font-medium flex items-center"><TrendingUp size={14} className="mr-1"/></span>
            <span className="text-slate-400">MRR Atual Base</span>
          </div>
        </Card>

        <Card className="p-5 flex flex-col gap-3 border-l-4 border-l-emerald-500">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm font-medium text-slate-500">Vendas Adicionais</p>
              <h3 className="text-2xl font-bold text-slate-800">R$ {salesThisMonth.toLocaleString('pt-BR')}</h3>
            </div>
            <div className="p-2 bg-emerald-50 rounded-lg text-emerald-600"><BarChart3 size={20} /></div>
          </div>
          <div className="flex items-center gap-1 text-sm">
            <span className="text-emerald-500 font-medium">{sales.filter(s => s.type !== 'cancelamento' && s.date && s.date.startsWith(currentMonthStr)).length}</span>
            <span className="text-slate-400">vendas no mês atual</span>
          </div>
        </Card>

        <Card className="p-5 flex flex-col gap-3 border-l-4 border-l-rose-500">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm font-medium text-slate-500">Custos (Mês)</p>
              <h3 className="text-2xl font-bold text-slate-800">R$ {expensesThisMonth.toLocaleString('pt-BR')}</h3>
            </div>
            <div className="p-2 bg-rose-50 rounded-lg text-rose-600"><TrendingDown size={20} /></div>
          </div>
          <div className="flex items-center gap-1 text-sm">
            <span className="text-slate-400 cursor-pointer hover:text-[#ff4d00] transition-colors" onClick={() => setTab('custos')}>Ver detalhes na aba Custos</span>
          </div>
        </Card>

        <Card className="p-5 flex flex-col gap-3 border-l-4 border-l-blue-500 bg-gradient-to-br from-white to-blue-50/30">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm font-medium text-slate-500">Lucro Líquido (Mês)</p>
              <h3 className="text-2xl font-bold text-slate-800">R$ {netProfit.toLocaleString('pt-BR')}</h3>
            </div>
            <div className="p-2 bg-blue-100 rounded-lg text-blue-600"><Wallet size={20} /></div>
          </div>
          <div className="flex items-center gap-1 text-sm">
            <span className={`${netProfit >= 0 ? 'text-emerald-500' : 'text-rose-500'} font-bold`}>
              Margem: {profitMargin}%
            </span>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-5">
          <div className="flex justify-between items-center mb-6">
            <h3 className="font-semibold text-slate-800">Evolução de Receita (MRR)</h3>
            <select className="text-sm border-slate-200 rounded-md text-slate-600 bg-slate-50">
              <option>Últimos 6 meses</option>
              <option>Este ano</option>
            </select>
          </div>
          
          <div className="relative h-64 w-full mt-4">
            <div 
              className="absolute left-0 right-0 border-t-2 border-dashed border-slate-300 z-0 flex justify-end" 
              style={{ top: `${goalTopPercent}%` }}
            >
              <span className="text-slate-500 font-bold text-xs bg-white pl-2 -mt-2.5">
                Meta: R$ {targetRevenue.toLocaleString('pt-BR')}
              </span>
            </div>

            <div className="absolute inset-0 flex items-end justify-between gap-2 px-2 pb-6 z-10 pt-8 border-b border-slate-200">
              {chartData.map((item, i) => (
                <div key={i} className="flex flex-col items-center flex-1 group h-full justify-end relative">
                  <div className="absolute opacity-0 group-hover:opacity-100 bottom-full mb-2 bg-slate-800 text-white text-xs py-1 px-2 rounded transition-opacity whitespace-nowrap pointer-events-none z-20">
                    R$ {item.val.toLocaleString('pt-BR')}
                  </div>
                  <div 
                    className={`w-10/12 sm:w-14 rounded-t-md transition-all duration-500 
                      ${item.isFuture ? 'bg-slate-100' : 'bg-[#ff4d00] group-hover:bg-[#d63a00] shadow-sm'}`} 
                    style={{ 
                      height: `${(item.val / chartMax) * 100}%`, 
                      minHeight: item.isFuture ? '0' : (item.val === 0 ? '4px' : '4px') 
                    }}
                  ></div>
                  <span className={`absolute -bottom-6 text-xs font-medium ${item.isFuture ? 'text-slate-400' : 'text-slate-700'}`}>
                    {item.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </Card>

        <Card className="p-5 flex flex-col items-center justify-center">
          <div className="w-full flex justify-between items-center mb-6">
            <h3 className="font-semibold text-slate-800">Meta Geral (Base Atual)</h3>
            <button onClick={() => onEditGoal('revenue')} className="text-slate-400 hover:text-[#ff4d00] transition-colors p-1" title="Editar Meta Geral">
              <Edit size={16} />
            </button>
          </div>
          
          <div className="relative w-40 h-40 flex items-center justify-center">
            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
              <path className="text-slate-100" strokeWidth="3" stroke="currentColor" fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
              <path className="text-[#ff4d00]" strokeWidth="3" strokeDasharray={`${goalPercentage}, 100`} strokeLinecap="round" stroke="currentColor" fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
            </svg>
            <div className="absolute flex flex-col items-center">
              <span className="text-3xl font-bold text-slate-800">{goalPercentage}%</span>
              <span className="text-xs text-slate-500">Atingido</span>
            </div>
          </div>
          <div className="mt-6 text-center">
            <p className="text-sm text-slate-500">
              {targetRevenue > currentRevenue 
                ? <>Faltam <strong className="text-slate-800">R$ {(targetRevenue - currentRevenue).toLocaleString('pt-BR')}</strong> para atingir a meta geral.</>
                : <strong className="text-emerald-500">Meta Geral Superada! 🎉</strong>
              }
            </p>
          </div>
        </Card>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="flex flex-col h-full">
          <div className="p-5 border-b border-slate-100 flex justify-between items-center">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2"><AlertCircle size={18} className="text-rose-500" /> Tarefas Prioritárias</h3>
            <button onClick={() => setTab('tarefas')} className="text-sm text-[#ff4d00] font-medium hover:underline">Ver todas</button>
          </div>
          <div className="p-0">
            {tasks.filter(t => t.priority === 'alta' || t.status === 'atrasada').slice(0, 4).map(task => {
              const client = clients.find(c => c.id === task.clientId);
              return (
                <div key={task.id} className="p-4 border-b border-slate-50 last:border-0 hover:bg-slate-50 transition-colors flex items-start gap-3">
                  <div className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${task.status === 'atrasada' ? 'bg-rose-500' : 'bg-amber-500'}`}></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{task.title}</p>
                    <p className="text-xs text-slate-500 truncate">{client?.name} • Prazo: {new Date(task.deadline).toLocaleDateString('pt-BR')}</p>
                  </div>
                  <Badge type={task.status === 'atrasada' ? 'danger' : 'warning'}>{task.status === 'atrasada' ? 'Atrasada' : 'Alta Prioridade'}</Badge>
                </div>
              )
            })}
            {tasks.filter(t => t.priority === 'alta' || t.status === 'atrasada').length === 0 && (
              <p className="text-sm text-slate-500 text-center py-6">Nenhuma tarefa prioritária no momento.</p>
            )}
          </div>
        </Card>

        <Card className="flex flex-col h-full">
          <div className="p-5 border-b border-slate-100 flex justify-between items-center">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2"><Users size={18} className="text-[#ff4d00]" /> Clientes Recentes</h3>
            <button onClick={() => setTab('clientes')} className="text-sm text-[#ff4d00] font-medium hover:underline">Gerenciar</button>
          </div>
          <div className="p-0">
            {[...clients].sort((a,b) => new Date(b.startDate) - new Date(a.startDate)).slice(0, 4).map(client => (
              <div key={client.id} className="p-4 border-b border-slate-50 last:border-0 hover:bg-slate-50 transition-colors flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-800">{client.name}</p>
                  <p className="text-xs text-slate-500">{client.niche}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-slate-800">R$ {client.mrr.toLocaleString('pt-BR')}</p>
                  <Badge type={client.status === 'ativo' ? 'success' : 'default'} className="mt-1">{client.status.toUpperCase()}</Badge>
                </div>
              </div>
            ))}
            {clients.length === 0 && (
              <p className="text-sm text-slate-500 text-center py-6">Nenhum cliente cadastrado.</p>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

const CustosTab = ({ expenses, onOpenNewExpense, onDeleteExpense }) => {
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const currentMonthStr = new Date().toISOString().slice(0, 7); 
  
  const expensesThisMonth = expenses.filter(e => e.date && e.date.startsWith(currentMonthStr));
  const totalCost = expensesThisMonth.reduce((acc, curr) => acc + curr.value, 0);
  const fixedCost = expensesThisMonth.filter(e => e.type === 'fixo').reduce((acc, curr) => acc + curr.value, 0);
  const variableCost = expensesThisMonth.filter(e => e.type === 'variavel').reduce((acc, curr) => acc + curr.value, 0);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Despesas e Custos</h2>
          <p className="text-sm text-slate-500">Controle o caixa da agência e analise para onde está indo o dinheiro.</p>
        </div>
        <button onClick={onOpenNewExpense} className="bg-slate-800 hover:bg-slate-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2">
          <Plus size={16} /> Registrar Despesa
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-5 border-l-4 border-l-rose-500">
          <p className="text-sm font-medium text-slate-500">Custo Total (Mês Atual)</p>
          <h3 className="text-2xl font-bold text-slate-800 mt-1">R$ {totalCost.toLocaleString('pt-BR')}</h3>
        </Card>
        <Card className="p-5 border-l-4 border-l-slate-400">
          <p className="text-sm font-medium text-slate-500">Custos Fixos</p>
          <h3 className="text-2xl font-bold text-slate-800 mt-1">R$ {fixedCost.toLocaleString('pt-BR')}</h3>
        </Card>
        <Card className="p-5 border-l-4 border-l-amber-500">
          <p className="text-sm font-medium text-slate-500">Custos Variáveis</p>
          <h3 className="text-2xl font-bold text-slate-800 mt-1">R$ {variableCost.toLocaleString('pt-BR')}</h3>
        </Card>
      </div>

      <Card className="p-5">
        <h3 className="font-semibold text-slate-800 mb-6">Histórico de Despesas</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="text-slate-500 text-xs uppercase tracking-wider border-b border-slate-100">
                <th className="pb-3 font-semibold p-4">Descrição</th>
                <th className="pb-3 font-semibold p-4">Categoria</th>
                <th className="pb-3 font-semibold p-4">Tipo</th>
                <th className="pb-3 font-semibold p-4">Data</th>
                <th className="pb-3 font-semibold p-4 text-right">Valor</th>
                <th className="pb-3 font-semibold p-4 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {[...expenses].sort((a,b) => new Date(b.date) - new Date(a.date)).map(expense => (
                <tr key={expense.id} className="hover:bg-slate-50 transition-colors">
                  <td className="p-4 font-medium text-slate-800 text-sm">{expense.description}</td>
                  <td className="p-4 text-sm text-slate-600">{expense.category}</td>
                  <td className="p-4">
                    <Badge type={expense.type === 'fixo' ? 'default' : 'warning'}>{expense.type.toUpperCase()}</Badge>
                  </td>
                  <td className="p-4 text-sm text-slate-600">{new Date(expense.date).toLocaleDateString('pt-BR')}</td>
                  <td className="p-4 text-right font-semibold text-rose-600">R$ {expense.value.toLocaleString('pt-BR')}</td>
                  <td className="p-4 text-right">
                    {confirmDeleteId === expense.id ? (
                      <button onClick={() => { onDeleteExpense(expense.id); setConfirmDeleteId(null); }} className="text-rose-600 bg-rose-50 px-2 py-1 rounded text-xs font-bold hover:bg-rose-100 transition-colors">Excluir?</button>
                    ) : (
                      <button onClick={() => setConfirmDeleteId(expense.id)} className="text-slate-400 hover:text-rose-500 p-1 transition-colors"><Trash2 size={16} /></button>
                    )}
                  </td>
                </tr>
              ))}
              {expenses.length === 0 && <tr><td colSpan="6" className="py-8 text-center text-slate-500">Nenhuma despesa registrada.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

const ClientesTab = ({ clients, users, onSelectClient, onOpenNewClient, onDeleteClient, onEditClient, searchQuery, setSearchQuery }) => {
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Clientes</h2>
          <p className="text-sm text-slate-500">Gerencie todos os clientes da Mind Assessoria.</p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
            <input 
              type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar cliente..." 
              className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#ff4d00]/50"
            />
          </div>
          <button onClick={onOpenNewClient} className="bg-[#ff4d00] hover:bg-[#d63a00] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2">
            <Plus size={16} /> Novo
          </button>
        </div>
      </div>

      <Card className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider border-b border-slate-200">
              <th className="p-4 font-semibold">Cliente / Nicho</th>
              <th className="p-4 font-semibold">Valor (MRR)</th>
              <th className="p-4 font-semibold">Status</th>
              <th className="p-4 font-semibold">Responsáveis</th>
              <th className="p-4 font-semibold">Serviços</th>
              <th className="p-4 font-semibold text-right">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {clients.map(client => {
              const clientOwners = users.filter(u => (client.ownerIds || [client.ownerId]).includes(u.id));
              return (
                <tr key={client.id} className="hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => onSelectClient(client)}>
                  <td className="p-4">
                    <p className="font-medium text-slate-800">{client.name}</p>
                    <p className="text-xs text-slate-500">{client.niche}</p>
                  </td>
                  <td className="p-4 font-medium text-slate-700">R$ {client.mrr.toLocaleString('pt-BR')}</td>
                  <td className="p-4"><Badge type={client.status === 'ativo' ? 'success' : 'default'}>{client.status.toUpperCase()}</Badge></td>
                  <td className="p-4">
                    <div className="flex items-center">
                      {clientOwners.slice(0, 3).map((owner, i) => (
                        <div key={owner.id} className={i > 0 ? '-ml-2' : ''} style={{ zIndex: 10 - i }}>
                          <Avatar src={owner.avatar} name={owner.name} size="sm" />
                        </div>
                      ))}
                      {clientOwners.length > 3 && (
                        <div className="w-8 h-8 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-[10px] font-bold text-slate-600 -ml-2 z-0">
                          +{clientOwners.length - 3}
                        </div>
                      )}
                      {clientOwners.length === 0 && <span className="text-xs text-slate-400">Nenhum</span>}
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex flex-wrap gap-1">
                      {client.services.map((srv, i) => (
                        <span key={i} className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded border border-slate-200">{srv}</span>
                      ))}
                    </div>
                  </td>
                  <td className="p-4 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => onEditClient(client)} className="text-slate-400 hover:text-blue-500 p-1 transition-colors" title="Editar"><Edit size={16} /></button>
                      {confirmDeleteId === client.id ? (
                        <button onClick={() => { onDeleteClient(client.id); setConfirmDeleteId(null); }} className="text-rose-600 bg-rose-50 px-2 py-1 rounded text-xs font-bold hover:bg-rose-100 transition-colors">Excluir?</button>
                      ) : (
                        <button onClick={() => setConfirmDeleteId(client.id)} className="text-slate-400 hover:text-rose-500 p-1 transition-colors" title="Excluir"><Trash2 size={16} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              )
            })}
            {clients.length === 0 && (
               <tr><td colSpan="6" className="p-8 text-center text-slate-500">Nenhum cliente encontrado.</td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
};

const ClientDetail = ({ client, onBack, tasks, users, onOpenNewTask }) => {
  const clientTasks = tasks.filter(t => t.clientId === client.id);
  const clientOwners = users.filter(u => (client.ownerIds || [client.ownerId]).includes(u.id));

  const getStatusText = (status) => {
    if (status === 'em_andamento') return 'Em Andamento';
    if (status === 'concluida') return 'Concluída';
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <button onClick={onBack} className="text-slate-500 hover:text-slate-800 flex items-center gap-2 text-sm font-medium mb-2 transition-colors">
        <ArrowLeft size={16} /> Voltar para a lista
      </button>

      <div className="flex flex-col md:flex-row gap-6">
        <div className="flex-1 space-y-6">
          <Card className="p-6">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-2xl font-bold text-slate-800">{client.name}</h2>
                <p className="text-slate-500">{client.niche} • Cliente desde {new Date(client.startDate).toLocaleDateString('pt-BR')}</p>
              </div>
              <Badge type={client.status === 'ativo' ? 'success' : 'default'} className="text-sm">{client.status.toUpperCase()}</Badge>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-4 border-t border-slate-100">
              <div>
                <p className="text-xs text-slate-500 mb-1">Valor Mensal (MRR)</p>
                <p className="font-semibold text-slate-800">R$ {client.mrr.toLocaleString('pt-BR')}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Próximo Vencimento</p>
                <p className="font-semibold text-slate-800">{new Date(client.nextDue).toLocaleDateString('pt-BR')}</p>
              </div>
              <div className="col-span-2">
                <p className="text-xs text-slate-500 mb-1">Responsáveis na Agência</p>
                <div className="flex flex-wrap gap-2">
                  {clientOwners.map(owner => (
                    <div key={owner.id} className="flex items-center gap-1.5 bg-slate-50 pr-2 rounded-full border border-slate-200">
                      <Avatar src={owner.avatar} size="sm" name={owner.name} />
                      <span className="font-medium text-slate-800 text-xs">{owner.name.split(' ')[0]}</span>
                    </div>
                  ))}
                  {clientOwners.length === 0 && <span className="text-sm text-slate-400">Nenhum responsável</span>}
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="font-semibold text-slate-800 mb-4">Observações e Histórico</h3>
            <div className="bg-amber-50 border border-amber-100 rounded-lg p-4 mb-4">
              <p className="text-sm text-amber-800 leading-relaxed">{client.notes || "Nenhuma observação adicionada."}</p>
            </div>
          </Card>
        </div>

        <div className="w-full md:w-80 space-y-6">
          <Card className="p-5">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-slate-800">Tarefas Vinculadas</h3>
              <Badge type="brand">{clientTasks.length}</Badge>
            </div>
            <div className="space-y-3">
              {clientTasks.length > 0 ? clientTasks.map(task => (
                <div key={task.id} className="p-3 border border-slate-100 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                  <p className="text-sm font-medium text-slate-800 mb-1">{task.title}</p>
                  <div className="flex justify-between items-center text-xs">
                    <Badge type={task.status === 'concluida' ? 'success' : task.status === 'atrasada' ? 'danger' : 'warning'}>{getStatusText(task.status)}</Badge>
                    <span className="text-slate-500 flex items-center gap-1"><Clock size={12} /> {new Date(task.deadline).toLocaleDateString('pt-BR', {day: '2-digit', month: 'short'})}</span>
                  </div>
                </div>
              )) : (
                <p className="text-sm text-slate-500 text-center py-4">Nenhuma tarefa encontrada.</p>
              )}
            </div>
            <button onClick={onOpenNewTask} className="w-full mt-4 border border-dashed border-slate-300 text-slate-600 rounded-lg py-2 text-sm font-medium hover:border-[#ff4d00] hover:text-[#ff4d00] transition-colors">
              + Adicionar Tarefa
            </button>
          </Card>
        </div>
      </div>
    </div>
  );
};

const TarefasTab = ({ tasks, clients, users, onOpenNewTask, onUpdateTaskStatus, onDeleteTask }) => {
  const [viewMode, setViewMode] = useState('kanban');
  const [draggedTaskId, setDraggedTaskId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const columns = [
    { id: 'pendente', title: 'Pendentes', color: 'bg-slate-200' },
    { id: 'em_andamento', title: 'Em Andamento', color: 'bg-[#ff4d00]/30' },
    { id: 'atrasada', title: 'Atrasadas', color: 'bg-rose-200' },
    { id: 'concluida', title: 'Concluídas', color: 'bg-emerald-200' },
  ];

  const getPriorityText = (priority) => {
    if (priority === 'media') return 'Média';
    return priority.charAt(0).toUpperCase() + priority.slice(1);
  };

  const getStatusText = (status) => {
    if (status === 'em_andamento') return 'Em Andamento';
    if (status === 'concluida') return 'Concluída';
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  return (
    <div className="space-y-6 h-full flex flex-col animate-fade-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Tarefas</h2>
          <p className="text-sm text-slate-500">Gerenciamento operacional da equipe.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="bg-slate-100 p-1 rounded-lg flex">
            <button onClick={() => setViewMode('kanban')} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${viewMode === 'kanban' ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500 hover:text-slate-700'}`}>Kanban</button>
            <button onClick={() => setViewMode('list')} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${viewMode === 'list' ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500 hover:text-slate-700'}`}>Lista</button>
          </div>
          <button onClick={onOpenNewTask} className="bg-[#ff4d00] hover:bg-[#d63a00] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2">
            <Plus size={16} /> Nova Tarefa
          </button>
        </div>
      </div>

      {viewMode === 'kanban' ? (
        <div className="flex gap-6 overflow-x-auto pb-4 flex-1">
          {columns.map(col => (
            <div 
              key={col.id} 
              className={`min-w-[300px] w-80 flex flex-col gap-3 p-2 -m-2 rounded-xl transition-all ${draggedTaskId ? 'bg-slate-100/50 outline-dashed outline-2 outline-slate-300' : ''}`}
              onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
              onDrop={(e) => {
                e.preventDefault();
                const taskId = e.dataTransfer.getData('text/plain') || draggedTaskId;
                if (taskId) { 
                  onUpdateTaskStatus(taskId, col.id); 
                  setDraggedTaskId(null); 
                }
              }}
            >
              <div className="flex justify-between items-center mb-2 px-2">
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${col.color}`}></div>
                  <h3 className="font-semibold text-slate-700">{col.title}</h3>
                </div>
                <Badge>{tasks.filter(t => t.status === col.id).length}</Badge>
              </div>
              
              <div className="flex flex-col gap-3">
                {tasks.filter(t => t.status === col.id).map(task => {
                  const client = clients.find(c => c.id === task.clientId);
                  const owner = users.find(u => u.id === task.ownerId);
                  return (
                    <Card 
                      key={task.id} 
                      className={`p-4 cursor-grab active:cursor-grabbing hover:border-[#ff4d00]/50 transition-all ${draggedTaskId === task.id ? 'opacity-40 border-dashed border-[#ff4d00] scale-95 shadow-none' : ''}`}
                      draggable
                      onDragStart={(e) => {
                        setDraggedTaskId(task.id);
                        e.dataTransfer.effectAllowed = 'move';
                        e.dataTransfer.setData('text/plain', task.id);
                      }}
                      onDragEnd={() => setDraggedTaskId(null)}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <Badge type={task.priority === 'alta' ? 'danger' : task.priority === 'media' ? 'warning' : 'brand'}>
                          {getPriorityText(task.priority)}
                        </Badge>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-medium uppercase text-slate-400 bg-slate-100 px-2 py-0.5 rounded">{task.type}</span>
                          {confirmDeleteId === task.id ? (
                            <button onClick={() => { onDeleteTask(task.id); setConfirmDeleteId(null); }} className="text-rose-600 bg-rose-50 px-2 py-0.5 rounded text-xs font-bold hover:bg-rose-100">Excluir?</button>
                          ) : (
                            <button onClick={() => setConfirmDeleteId(task.id)} className="text-slate-300 hover:text-rose-500 transition-colors"><Trash2 size={14} /></button>
                          )}
                        </div>
                      </div>
                      <h4 className="font-medium text-slate-800 text-sm mb-1">{task.title}</h4>
                      <p className="text-xs text-slate-500 mb-4">{client?.name || 'Cliente Removido'}</p>
                      
                      {task.description && (
                        <div className="mb-3 text-xs text-slate-600 bg-slate-50 p-2 rounded border border-slate-100 whitespace-pre-wrap cursor-text" onClick={(e) => e.stopPropagation()}>
                          {formatTextWithLinks(task.description)}
                        </div>
                      )}

                      <div className="flex justify-between items-center pt-3 border-t border-slate-100">
                        <Avatar src={owner?.avatar} name={owner?.name} size="sm" />
                        <div className={`flex items-center gap-1 text-xs ${getDeadlineStyle(task.deadline, task.status)}`}>
                          <Clock size={12} />
                          <span>
                            {new Date(task.deadline).toLocaleDateString('pt-BR', {day: '2-digit', month: '2-digit'})}
                          </span>
                        </div>
                      </div>
                    </Card>
                  )
                })}
                <button onClick={onOpenNewTask} className="w-full border-2 border-dashed border-slate-200 text-slate-400 hover:text-slate-600 hover:border-slate-300 hover:bg-slate-50 rounded-xl py-3 text-sm font-medium transition-colors flex items-center justify-center gap-1">
                  <Plus size={16} /> Adicionar
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Card className="overflow-x-auto">
           <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider border-b border-slate-200">
                <th className="p-4 font-semibold">Tarefa</th>
                <th className="p-4 font-semibold">Cliente</th>
                <th className="p-4 font-semibold">Status</th>
                <th className="p-4 font-semibold">Prioridade</th>
                <th className="p-4 font-semibold">Prazo</th>
                <th className="p-4 font-semibold">Responsável</th>
                <th className="p-4 font-semibold text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {tasks.map(task => {
                const client = clients.find(c => c.id === task.clientId);
                const owner = users.find(u => u.id === task.ownerId);
                return (
                  <tr key={task.id} className="hover:bg-slate-50 transition-colors">
                    <td className="p-4 font-medium text-slate-800 text-sm">{task.title}</td>
                    <td className="p-4 text-sm text-slate-600">{client?.name || 'Cliente Removido'}</td>
                    <td className="p-4">
                      <select value={task.status} onChange={(e) => onUpdateTaskStatus(task.id, e.target.value)} className="text-xs font-medium bg-slate-50 border border-slate-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#ff4d00]">
                        <option value="pendente">Pendente</option>
                        <option value="em_andamento">Em Andamento</option>
                        <option value="atrasada">Atrasada</option>
                        <option value="concluida">Concluída</option>
                      </select>
                    </td>
                    <td className="p-4"><Badge type={task.priority === 'alta' ? 'danger' : task.priority === 'media' ? 'warning' : 'brand'}>{getPriorityText(task.priority)}</Badge></td>
                    <td className={`p-4 text-sm ${getDeadlineStyle(task.deadline, task.status)}`}>
                      <div className="flex items-center gap-1.5">
                        <Clock size={14} />
                        {new Date(task.deadline).toLocaleDateString('pt-BR')}
                      </div>
                    </td>
                    <td className="p-4 flex items-center gap-2"><Avatar src={owner?.avatar} name={owner?.name} size="sm" /></td>
                    <td className="p-4 text-right">
                      {confirmDeleteId === task.id ? (
                        <button onClick={() => { onDeleteTask(task.id); setConfirmDeleteId(null); }} className="text-rose-600 bg-rose-50 px-2 py-1 rounded text-xs font-bold hover:bg-rose-100">Excluir?</button>
                      ) : (
                        <button onClick={() => setConfirmDeleteId(task.id)} className="text-slate-400 hover:text-rose-500 p-1 transition-colors"><Trash2 size={16} /></button>
                      )}
                    </td>
                  </tr>
                )
              })}
              {tasks.length === 0 && <tr><td colSpan="7" className="p-8 text-center text-slate-500">Nenhuma tarefa encontrada.</td></tr>}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
};

const VendasTab = ({ sales, clients, goals, onOpenNewSale, onDeleteSale, onEditGoal }) => {
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  
  const targetSales = goals.newSales || 10000;
  const currentMonthStr = new Date().toISOString().slice(0, 7); 
  
  const salesThisMonth = sales
    .filter(s => s.type !== 'cancelamento' && s.date && s.date.startsWith(currentMonthStr))
    .reduce((acc, curr) => acc + curr.value, 0);

  const mrrAdded = sales
    .filter(s => (s.type === 'novo_contrato' || s.type === 'upsell') && s.date && s.date.startsWith(currentMonthStr))
    .reduce((acc, curr) => acc + curr.value, 0);
  
  const churnMRR = sales
    .filter(s => s.type === 'cancelamento' && s.date && s.date.startsWith(currentMonthStr))
    .reduce((acc, curr) => acc + curr.value, 0);

  const baseAtual = clients.filter(c => c.status === 'ativo').reduce((acc, curr) => acc + curr.mrr, 0);
  const churnRate = baseAtual > 0 ? ((churnMRR / (baseAtual + churnMRR)) * 100).toFixed(1) : 0;
  
  const salesPercentage = Math.min((mrrAdded / targetSales) * 100, 100).toFixed(1);
  const remainingSales = Math.max(targetSales - mrrAdded, 0);

  const getSaleTypeText = (type) => {
    if (type === 'novo_contrato') return 'NOVO CONTRATO';
    if (type === 'projeto_pontual') return 'PROJETO PONTUAL';
    if (type === 'cancelamento') return 'CANCELAMENTO / CHURN';
    return type.toUpperCase();
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Vendas e Faturamento</h2>
          <p className="text-sm text-slate-500">Acompanhamento de novos contratos e movimentações do mês.</p>
        </div>
        <button onClick={onOpenNewSale} className="bg-[#ff4d00] hover:bg-[#d63a00] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2">
          <Plus size={16} /> Registrar Venda
        </button>
      </div>

      <Card className="p-6 bg-gradient-to-r from-slate-900 to-slate-800 text-white border-none shadow-md">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end mb-4 gap-4">
          <div>
            <h3 className="text-lg font-bold flex items-center gap-2">
              Meta de Novas Vendas (Mês)
              <button onClick={() => onEditGoal('sales')} className="text-slate-400 hover:text-white transition-colors p-1" title="Editar Meta do Mês">
                <Edit size={16} />
              </button>
            </h3>
            <p className="text-slate-400 text-sm mt-1">
              {remainingSales > 0 
                ? `Faltam R$ ${remainingSales.toLocaleString('pt-BR')} para atingir a meta.` 
                : 'Parabéns! A meta de vendas do mês foi batida! 🎉'}
            </p>
          </div>
          <div className="text-right">
            <span className="text-3xl font-bold text-[#ff4d00]">{salesPercentage}%</span>
          </div>
        </div>
        <div className="w-full bg-slate-700/50 rounded-full h-4 overflow-hidden border border-slate-600/50">
          <div className="bg-gradient-to-r from-[#ff4d00] to-[#ff7a40] h-4 rounded-full transition-all duration-1000 relative" style={{ width: `${salesPercentage}%` }}>
            <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
          </div>
        </div>
        <div className="flex justify-between text-xs text-slate-400 mt-2 font-medium">
          <span>R$ 0</span>
          <span>Meta: R$ {targetSales.toLocaleString('pt-BR')}</span>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-5 border-l-4 border-l-[#ff4d00]">
          <p className="text-sm font-medium text-slate-500">Total Vendido (Mês)</p>
          <h3 className="text-2xl font-bold text-slate-800 mt-1">R$ {salesThisMonth.toLocaleString('pt-BR')}</h3>
        </Card>
        <Card className="p-5 border-l-4 border-l-emerald-500">
          <p className="text-sm font-medium text-slate-500">Novo MRR Adicionado</p>
          <h3 className="text-2xl font-bold text-slate-800 mt-1">R$ {mrrAdded.toLocaleString('pt-BR')}</h3>
        </Card>
        <Card className="p-5 border-l-4 border-l-purple-500">
          <p className="text-sm font-medium text-slate-500">Ticket Médio (Mês)</p>
          <h3 className="text-2xl font-bold text-slate-800 mt-1">R$ {(salesThisMonth / Math.max(sales.filter(s => s.type !== 'cancelamento' && s.date?.startsWith(currentMonthStr)).length, 1)).toLocaleString('pt-BR')}</h3>
        </Card>
        <Card className="p-5 border-l-4 border-l-rose-500">
          <p className="text-sm font-medium text-slate-500">Churn Rate (Mês)</p>
          <div className="flex items-end gap-2 mt-1">
            <h3 className="text-2xl font-bold text-slate-800">{churnRate}%</h3>
            <span className="text-sm font-medium text-rose-500 mb-1">- R$ {churnMRR.toLocaleString('pt-BR')}</span>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-5">
          <h3 className="font-semibold text-slate-800 mb-6">Movimentações Recentes (Todas)</h3>
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="text-slate-500 text-xs uppercase tracking-wider border-b border-slate-100">
                <th className="pb-3 font-semibold">Cliente</th>
                <th className="pb-3 font-semibold">Tipo</th>
                <th className="pb-3 font-semibold">Data</th>
                <th className="pb-3 font-semibold text-right">Valor</th>
                <th className="pb-3 font-semibold text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {[...sales].sort((a,b) => new Date(b.date) - new Date(a.date)).map(sale => {
                const client = clients.find(c => c.id === sale.clientId);
                return (
                  <tr key={sale.id}>
                    <td className="py-3 font-medium text-slate-800 text-sm">{client?.name || 'Cliente Removido'}</td>
                    <td className="py-3"><Badge type={sale.type === 'novo_contrato' ? 'success' : sale.type === 'cancelamento' ? 'danger' : 'brand'}>{getSaleTypeText(sale.type)}</Badge></td>
                    <td className="py-3 text-sm text-slate-600">{new Date(sale.date).toLocaleDateString('pt-BR')}</td>
                    <td className="py-3 text-right font-semibold text-slate-800">R$ {sale.value.toLocaleString('pt-BR')}</td>
                    <td className="py-3 text-right">
                      {confirmDeleteId === sale.id ? (
                        <button onClick={() => { onDeleteSale(sale.id); setConfirmDeleteId(null); }} className="text-rose-600 bg-rose-50 px-2 py-1 rounded text-xs font-bold hover:bg-rose-100 transition-colors">Excluir?</button>
                      ) : (
                        <button onClick={() => setConfirmDeleteId(sale.id)} className="text-slate-400 hover:text-rose-500 p-1 transition-colors"><Trash2 size={16} /></button>
                      )}
                    </td>
                  </tr>
                )
              })}
              {sales.length === 0 && <tr><td colSpan="5" className="py-6 text-center text-slate-500">Nenhuma movimentação registrada.</td></tr>}
            </tbody>
          </table>
        </Card>

        <Card className="p-5">
           <h3 className="font-semibold text-slate-800 mb-4">Previsão de Receita (MRR)</h3>
           <div className="space-y-4">
              <div><div className="flex justify-between text-sm mb-1"><span className="text-slate-500">Base Atual (MRR)</span><span className="font-medium text-slate-800">R$ {baseAtual.toLocaleString('pt-BR')}</span></div></div>
              <div><div className="flex justify-between text-sm mb-1"><span className="text-slate-500">Novo MRR Vendido (Mês)</span><span className="font-medium text-emerald-600">+ R$ {mrrAdded.toLocaleString('pt-BR')}</span></div></div>
              <div><div className="flex justify-between text-sm mb-1"><span className="text-slate-500">Churn Registrado (Mês)</span><span className="font-medium text-rose-600">- R$ {churnMRR.toLocaleString('pt-BR')}</span></div></div>
              <div className="pt-4 border-t border-slate-100">
                <div className="flex justify-between">
                  <span className="font-semibold text-slate-800">Saldo Previsto</span>
                  <span className="font-bold text-[#ff4d00] text-lg">R$ {(baseAtual + mrrAdded - churnMRR).toLocaleString('pt-BR')}</span>
                </div>
              </div>
           </div>
        </Card>
      </div>
    </div>
  );
};

const NpsTab = ({ npsData, clients }) => {
  const [selectedClientForLink, setSelectedClientForLink] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);

  const totalResponses = npsData.length;
  const promoters = npsData.filter(n => n.score >= 9).length;
  const passives = npsData.filter(n => n.score >= 7 && n.score <= 8).length;
  const detractors = npsData.filter(n => n.score <= 6).length;

  const npsScore = totalResponses > 0 
    ? Math.round(((promoters - detractors) / totalResponses) * 100) 
    : 0;

  let npsZone = 'Aperfeiçoamento';
  let npsColor = 'text-amber-500';
  if (npsScore >= 75) { npsZone = 'Excelência'; npsColor = 'text-emerald-500'; }
  else if (npsScore >= 50) { npsZone = 'Qualidade'; npsColor = 'text-blue-500'; }
  else if (npsScore < 0) { npsZone = 'Crítica'; npsColor = 'text-rose-500'; }

  const handleCopyLink = () => {
    if (!selectedClientForLink) return;
    const url = `${window.location.origin}${window.location.pathname}?public_nps=true&clientId=${selectedClientForLink}`;
    navigator.clipboard.writeText(url);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 3000);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h2 className="text-2xl font-bold text-slate-800">NPS e Satisfação</h2>
        <p className="text-sm text-slate-500">Meça o nível de lealdade e satisfação dos seus clientes.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-6 flex flex-col items-center justify-center text-center col-span-1 md:col-span-2 bg-gradient-to-br from-slate-900 to-slate-800 text-white border-none shadow-md">
          <p className="text-slate-400 text-sm font-medium mb-2">Net Promoter Score (NPS Geral)</p>
          <div className="flex items-center gap-4">
            <h3 className={`text-6xl font-bold ${npsColor}`}>{npsScore}</h3>
          </div>
          <Badge className="mt-4 bg-white/10 text-white border border-white/20">Zona de {npsZone}</Badge>
        </Card>

        <Card className="p-5 flex flex-col justify-center border-l-4 border-l-emerald-500">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-slate-500">Promotores (9-10)</span>
            <Smile size={18} className="text-emerald-500" />
          </div>
          <h3 className="text-2xl font-bold text-slate-800">{promoters}</h3>
          <p className="text-xs text-slate-400 mt-1">{totalResponses > 0 ? Math.round((promoters/totalResponses)*100) : 0}% do total</p>
        </Card>

        <Card className="p-5 flex flex-col justify-center border-l-4 border-l-rose-500">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-slate-500">Detratores (0-6)</span>
            <Frown size={18} className="text-rose-500" />
          </div>
          <h3 className="text-2xl font-bold text-slate-800">{detractors}</h3>
          <p className="text-xs text-slate-400 mt-1">{totalResponses > 0 ? Math.round((detractors/totalResponses)*100) : 0}% do total</p>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-5">
          <h3 className="font-semibold text-slate-800 mb-6">Feedbacks Recebidos</h3>
          <div className="space-y-4">
            {[...npsData].sort((a,b) => new Date(b.date) - new Date(a.date)).map(nps => {
              const client = clients.find(c => c.id === nps.clientId);
              const isPromoter = nps.score >= 9;
              const isDetractor = nps.score <= 6;
              return (
                <div key={nps.id} className="p-4 border border-slate-100 rounded-xl bg-slate-50 flex flex-col sm:flex-row gap-4 items-start">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 font-bold text-lg ${
                    isPromoter ? 'bg-emerald-100 text-emerald-600' : isDetractor ? 'bg-rose-100 text-rose-600' : 'bg-amber-100 text-amber-600'
                  }`}>
                    {nps.score}
                  </div>
                  <div className="flex-1 w-full min-w-0">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-1 gap-1">
                      <h4 className="font-bold text-slate-800">{client?.name || 'Cliente Removido'}</h4>
                      <span className="text-xs text-slate-400 flex items-center gap-1"><Clock size={12}/> {new Date(nps.date).toLocaleDateString('pt-BR')}</span>
                    </div>
                    {nps.comment ? (
                      <p className="text-sm text-slate-600 bg-white p-3 rounded-lg border border-slate-100 mt-2 italic">"{nps.comment}"</p>
                    ) : (
                      <p className="text-xs text-slate-400 mt-1">Nenhum comentário deixado.</p>
                    )}
                  </div>
                </div>
              )
            })}
            {npsData.length === 0 && (
              <div className="text-center py-10">
                <HeartHandshake size={40} className="mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500 text-sm">Nenhuma avaliação de NPS recebida ainda.</p>
              </div>
            )}
          </div>
        </Card>

        <Card className="p-5 flex flex-col h-fit">
          <h3 className="font-semibold text-slate-800 mb-4">Gerar Link de Avaliação</h3>
          <p className="text-sm text-slate-500 mb-4 leading-relaxed">
            Selecione o cliente para gerar um link exclusivo da pesquisa de satisfação. Você pode enviar este link pelo WhatsApp ou E-mail.
          </p>
          
          <label className="block text-xs font-medium text-slate-700 mb-1">Selecione o Cliente</label>
          <select 
            value={selectedClientForLink} 
            onChange={(e) => setSelectedClientForLink(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-[#ff4d00] focus:ring-1 focus:ring-[#ff4d00] bg-white mb-4"
          >
            <option value="">Escolha um cliente...</option>
            {clients.filter(c => c.status === 'ativo').map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>

          <button 
            disabled={!selectedClientForLink}
            onClick={handleCopyLink}
            className={`w-full py-3 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
              selectedClientForLink ? (linkCopied ? 'bg-emerald-500 hover:bg-emerald-600 text-white' : 'bg-[#ff4d00] hover:bg-[#d63a00] text-white') : 'bg-slate-100 text-slate-400 cursor-not-allowed'
            }`}
          >
            {linkCopied ? <><CheckCircle2 size={16}/> Link Copiado!</> : <><Copy size={16}/> Copiar Link do Formulário</>}
          </button>

          {linkCopied && (
            <p className="text-xs text-emerald-600 text-center mt-3 animate-fade-in">
              O link está na sua área de transferência. Agora é só colar (Ctrl+V) onde quiser!
            </p>
          )}
        </Card>
      </div>
    </div>
  );
};

const EquipeTab = ({ users, clients, tasks, onOpenNewUser, onDeleteUser }) => {
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Equipe</h2>
          <p className="text-sm text-slate-500">Gestão de responsabilidades e produtividade.</p>
        </div>
        <button onClick={onOpenNewUser} className="bg-[#ff4d00] hover:bg-[#d63a00] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2">
          <Plus size={16} /> Adicionar Membro
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {users.map(user => {
          const userClients = clients.filter(c => (c.ownerIds || [c.ownerId]).includes(user.id)).length;
          const userTasks = tasks.filter(t => t.ownerId === user.id);
          const delayedTasks = userTasks.filter(t => t.status === 'atrasada');
          const completedTasks = userTasks.filter(t => t.status === 'concluida');
          const completionRate = userTasks.length > 0 ? Math.round((completedTasks.length / userTasks.length) * 100) : 0;

          return (
            <Card key={user.id} className="p-5 flex flex-col relative overflow-hidden">
              <div className={`absolute top-0 left-0 w-full h-1 ${user.color}`}></div>
              
              <div className="absolute top-4 right-4">
                {confirmDeleteId === user.id ? (
                  <button onClick={() => { onDeleteUser(user.id); setConfirmDeleteId(null); }} className="text-rose-600 bg-rose-50 px-2 py-1 rounded text-xs font-bold hover:bg-rose-100 transition-colors">Excluir?</button>
                ) : (
                  <button onClick={() => setConfirmDeleteId(user.id)} className="text-slate-400 hover:text-rose-500 p-1 transition-colors"><Trash2 size={16} /></button>
                )}
              </div>

              <div className="flex items-center gap-4 mb-6 mt-2">
                <Avatar src={user.avatar} name={user.name} size="lg" />
                <div>
                  <h3 className="font-bold text-slate-800">{user.name}</h3>
                  <p className="text-sm text-slate-500">{user.role}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-slate-50 p-3 rounded-lg text-center">
                  <p className="text-2xl font-bold text-slate-800">{userClients}</p>
                  <p className="text-xs text-slate-500">Clientes</p>
                </div>
                <div className="bg-slate-50 p-3 rounded-lg text-center">
                  <p className="text-2xl font-bold text-slate-800">{userTasks.length}</p>
                  <p className="text-xs text-slate-500">Tarefas totais</p>
                </div>
              </div>
              <div className="space-y-3 flex-1">
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-slate-600 font-medium">Taxa de Conclusão</span>
                    <span className="font-bold text-slate-800">{completionRate}%</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2">
                    <div className={`${user.color} h-2 rounded-full transition-all`} style={{ width: `${completionRate}%` }}></div>
                  </div>
                </div>
                {delayedTasks.length > 0 && (
                  <div className="mt-4 p-3 bg-rose-50 rounded-lg flex items-start gap-2 border border-rose-100">
                    <AlertCircle size={16} className="text-rose-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-rose-800">{delayedTasks.length} tarefa(s) atrasada(s)</p>
                      <p className="text-xs text-rose-600 mt-1">{delayedTasks[0].title}</p>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          )
        })}
        {users.length === 0 && (
          <p className="text-sm text-slate-500 py-6 col-span-full">Nenhum membro na equipe ainda.</p>
        )}
      </div>
    </div>
  );
};

// ==========================================
// COMPONENTE PRINCIPAL DO APP (CONTROLE DO FIREBASE E ROTAS)
// ==========================================
export default function App() {
  const [isPublicNpsRoute, setIsPublicNpsRoute] = useState(false);
  const [publicClientId, setPublicClientId] = useState(null);

  const [isInitAuth, setIsInitAuth] = useState(true);
  const [authUser, setAuthUser] = useState(null);
  const [loadingDb, setLoadingDb] = useState(false);
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);

  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [sales, setSales] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [npsData, setNpsData] = useState([]);
  const [goals, setGoals] = useState({ monthlyRevenue: 50000, newSales: 10000 });

  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedClient, setSelectedClient] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  
  const [isNewClientModalOpen, setIsNewClientModalOpen] = useState(false);
  const [isEditClientModalOpen, setIsEditClientModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [isNewTaskModalOpen, setIsNewTaskModalOpen] = useState(false);
  const [isNewSaleModalOpen, setIsNewSaleModalOpen] = useState(false);
  const [isNewExpenseModalOpen, setIsNewExpenseModalOpen] = useState(false);
  const [isNewUserModalOpen, setIsNewUserModalOpen] = useState(false);
  const [isEditGoalModalOpen, setIsEditGoalModalOpen] = useState(false);
  const [goalTypeToEdit, setGoalTypeToEdit] = useState(''); 
  const [isAddingNewClientInSale, setIsAddingNewClientInSale] = useState(false);
  
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const isNps = urlParams.get('public_nps') === 'true';
    const cId = urlParams.get('clientId');
    
    if (isNps && cId) {
      setIsPublicNpsRoute(true);
      setPublicClientId(cId);
      setIsInitAuth(false);
      return; 
    }

    const unsubscribe = onAuthStateChanged(auth, user => {
      setAuthUser(user);
      setIsInitAuth(false);
      if (user) setLoadingDb(true); 
    });
    return () => unsubscribe();
  }, []);

  const handleLogin = async (email, password) => {
    setAuthLoading(true);
    setAuthError('');
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      setAuthError('Credenciais inválidas. Verifique seu e-mail e senha.');
    }
    setAuthLoading(false);
  };

  const handleLogout = () => {
    signOut(auth);
  };

  useEffect(() => {
    if (!authUser) return;
    const basePath = `artifacts/${appId}/public/data`;

    const unsubUsers = onSnapshot(collection(db, `${basePath}/users`), (snapshot) => {
      if (snapshot.empty && INITIAL_USERS.length > 0) {
        INITIAL_USERS.forEach(u => setDoc(doc(db, `${basePath}/users`, u.id), u));
        INITIAL_CLIENTS.forEach(c => setDoc(doc(db, `${basePath}/clients`, c.id), c));
        INITIAL_TASKS.forEach(t => setDoc(doc(db, `${basePath}/tasks`, t.id), t));
        INITIAL_SALES.forEach(s => setDoc(doc(db, `${basePath}/sales`, s.id), s));
        INITIAL_EXPENSES.forEach(e => setDoc(doc(db, `${basePath}/expenses`, e.id), e));
        setLoadingDb(false);
      } else {
        setUsers(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoadingDb(false);
      }
    }, console.error);

    const unsubClients = onSnapshot(collection(db, `${basePath}/clients`), (snapshot) => {
      setClients(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    }, console.error);

    const unsubTasks = onSnapshot(collection(db, `${basePath}/tasks`), (snapshot) => {
      setTasks(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    }, console.error);

    const unsubSales = onSnapshot(collection(db, `${basePath}/sales`), (snapshot) => {
      setSales(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    }, console.error);
    
    const unsubExpenses = onSnapshot(collection(db, `${basePath}/expenses`), (snapshot) => {
      setExpenses(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    }, console.error);

    const unsubGoals = onSnapshot(doc(db, `${basePath}/settings`, 'goals'), (docSnap) => {
      if (docSnap.exists()) {
        setGoals(docSnap.data());
      } else {
        setDoc(doc(db, `${basePath}/settings`, 'goals'), { monthlyRevenue: 50000, newSales: 10000 });
      }
    }, console.error);

    const unsubNps = onSnapshot(collection(db, `${basePath}/nps`), (snapshot) => {
      setNpsData(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    }, console.error);

    return () => { unsubUsers(); unsubClients(); unsubTasks(); unsubSales(); unsubExpenses(); unsubGoals(); unsubNps(); };
  }, [authUser]);

  // CONTROLE DE PERMISSÕES
  const currentUserProfile = users.find(u => u.email === authUser?.email);
  const defaultPermissions = ['dashboard', 'clientes', 'tarefas', 'vendas', 'custos', 'nps', 'equipe'];
  const userPermissions = currentUserProfile?.permissions || defaultPermissions;

  useEffect(() => {
    if (userPermissions.length > 0 && !userPermissions.includes(activeTab)) {
      setActiveTab(userPermissions[0]);
    }
  }, [userPermissions, activeTab]);

  React.useEffect(() => { window.scrollTo(0, 0); }, [activeTab, selectedClient]);

  const filteredClients = clients.filter(c => c.name.toLowerCase().includes(searchQuery.toLowerCase()) || c.niche.toLowerCase().includes(searchQuery.toLowerCase()));
  const filteredTasks = tasks.filter(t => t.title.toLowerCase().includes(searchQuery.toLowerCase()));

  // SINO DE NOTIFICAÇÕES
  const notifications = React.useMemo(() => {
    const notifs = [];

    const delayedTasksList = tasks.filter(t => t.status === 'atrasada' || (t.status === 'pendente' && new Date(t.deadline) < new Date()));
    delayedTasksList.forEach(t => {
      const client = clients.find(c => c.id === t.clientId);
      notifs.push({
        id: `notif-delay-${t.id}`,
        type: 'warning',
        title: 'Tarefa Atrasada',
        message: `A tarefa "${t.title}" do cliente ${client?.name || 'Desconhecido'} precisa de atenção.`,
        time: 'Ação necessária',
        icon: <AlertCircle size={14} className="text-rose-500" />
      });
    });

    const currentRevenue = clients.filter(c => c.status === 'ativo').reduce((acc, curr) => acc + curr.mrr, 0);
    const targetRevenue = goals.monthlyRevenue || 50000;
    if (currentRevenue >= targetRevenue && targetRevenue > 0) {
      notifs.push({
        id: 'notif-goal-rev',
        type: 'success',
        title: 'Meta Geral Atingida! 🎉',
        message: 'Excelente trabalho! A meta de receita geral da agência foi superada.',
        time: 'Hoje',
        icon: <TrendingUp size={14} className="text-emerald-500" />
      });
    }

    const currentMonthStr = new Date().toISOString().slice(0, 7);
    const mrrAdded = sales.filter(s => (s.type === 'novo_contrato' || s.type === 'upsell') && s.date?.startsWith(currentMonthStr)).reduce((acc, curr) => acc + curr.value, 0);
    const targetSales = goals.newSales || 10000;
    if (mrrAdded >= targetSales && targetSales > 0) {
      notifs.push({
        id: 'notif-goal-sales',
        type: 'success',
        title: 'Meta de Vendas Atingida! 🚀',
        message: 'A equipe bateu a meta de novas vendas do mês.',
        time: 'Hoje',
        icon: <DollarSign size={14} className="text-emerald-500" />
      });
    }

    if (sales.length > 0) {
      const recentSales = [...sales].sort((a,b) => new Date(b.date) - new Date(a.date)).slice(0, 2);
      recentSales.forEach(s => {
        const client = clients.find(c => c.id === s.clientId);
        if (s.type !== 'cancelamento') {
           notifs.push({
              id: `notif-sale-${s.id}`,
              type: 'info',
              title: 'Nova Movimentação',
              message: `Novo ${s.type.replace('_', ' ')} de R$ ${s.value.toLocaleString('pt-BR')} do cliente ${client?.name || ''}.`,
              time: new Date(s.date).toLocaleDateString('pt-BR'),
              icon: <CheckCircle2 size={14} className="text-blue-500" />
           });
        }
      });
    }

    if (notifs.length === 0) {
       notifs.push({
         id: 'empty',
         type: 'empty',
         title: 'Tudo tranquilo',
         message: 'Nenhuma notificação nova por enquanto.',
         icon: <Bell size={14} className="text-slate-400" />
       });
    }

    return notifs;
  }, [tasks, clients, sales, goals]);

  const openEditGoalModal = (type) => {
    setGoalTypeToEdit(type);
    setIsEditGoalModalOpen(true);
  };

  const handleUpdateGoal = async (e) => {
    e.preventDefault();
    if (!authUser) return;
    const formData = new FormData(e.target);
    const newValue = Number(formData.get('goalValue'));
    
    const newGoals = { ...goals };
    if (goalTypeToEdit === 'revenue') newGoals.monthlyRevenue = newValue;
    else if (goalTypeToEdit === 'sales') newGoals.newSales = newValue;

    await setDoc(doc(db, `artifacts/${appId}/public/data/settings`, 'goals'), newGoals, { merge: true });
    setIsEditGoalModalOpen(false);
  };

  const handleAddClient = async (e) => {
    e.preventDefault();
    if(!authUser) return;
    const formData = new FormData(e.target);
    const newClient = {
      id: `c${Date.now()}`,
      name: formData.get('name'), niche: formData.get('niche'), mrr: Number(formData.get('mrr')),
      startDate: formData.get('startDate'), status: formData.get('status'), 
      ownerIds: formData.getAll('ownerIds'),
      services: formData.get('services').split(',').map(s => s.trim()), nextDue: '2026-05-15', notes: formData.get('notes')
    };
    await setDoc(doc(db, `artifacts/${appId}/public/data/clients`, newClient.id), newClient);
    setIsNewClientModalOpen(false);
    e.target.reset();
  };

  const handleUpdateClient = async (e) => {
    e.preventDefault();
    if(!authUser || !editingClient) return;
    const formData = new FormData(e.target);
    
    const updatedClient = {
      ...editingClient,
      name: formData.get('name'), niche: formData.get('niche'), mrr: Number(formData.get('mrr')),
      startDate: formData.get('startDate'), status: formData.get('status'), 
      ownerIds: formData.getAll('ownerIds'),
      services: formData.get('services').split(',').map(s => s.trim()), notes: formData.get('notes')
    };
    
    await updateDoc(doc(db, `artifacts/${appId}/public/data/clients`, editingClient.id), updatedClient);
    setIsEditClientModalOpen(false);
    setEditingClient(null);
    if(selectedClient?.id === editingClient.id) setSelectedClient(updatedClient);
  };

  const openEditClient = (client) => {
    setEditingClient(client);
    setIsEditClientModalOpen(true);
  };

  const handleAddTask = async (e) => {
    e.preventDefault();
    if(!authUser) return;
    const formData = new FormData(e.target);
    const newTask = {
      id: `t${Date.now()}`,
      title: formData.get('title'), 
      description: formData.get('description'), 
      clientId: formData.get('clientId'), 
      ownerId: formData.get('ownerId'),
      priority: formData.get('priority'), 
      deadline: formData.get('deadline'), 
      status: formData.get('status'),
      type: formData.get('type'), 
      recurrence: 'unica'
    };
    await setDoc(doc(db, `artifacts/${appId}/public/data/tasks`, newTask.id), newTask);
    setIsNewTaskModalOpen(false);
    e.target.reset();
  };

  const handleAddSale = async (e) => {
    e.preventDefault();
    if(!authUser) return;
    const formData = new FormData(e.target);
    
    const isNewClient = formData.get('isNewClient') === 'true';
    let finalClientId = formData.get('clientId');
    
    if (isNewClient) {
      finalClientId = `c${Date.now()}`;
      const newClient = {
        id: finalClientId,
        name: formData.get('newClientName'),
        niche: formData.get('newClientNiche') || '',
        mrr: Number(formData.get('value')), 
        startDate: formData.get('date'),
        status: 'ativo',
        ownerIds: formData.getAll('ownerIds'),
        services: [],
        notes: 'Cadastrado automaticamente via Registro de Venda.'
      };
      await setDoc(doc(db, `artifacts/${appId}/public/data/clients`, newClient.id), newClient);
    }

    const newSale = {
      id: `s${Date.now() + 1}`,
      clientId: finalClientId,
      value: Number(formData.get('value')),
      date: formData.get('date'),
      type: isNewClient ? 'novo_contrato' : formData.get('type')
    };
    
    await setDoc(doc(db, `artifacts/${appId}/public/data/sales`, newSale.id), newSale);
    setIsNewSaleModalOpen(false);
    setIsAddingNewClientInSale(false); 
    e.target.reset();
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();
    if(!authUser) return;
    const formData = new FormData(e.target);
    const newExpense = {
      id: `e${Date.now()}`,
      description: formData.get('description'),
      category: formData.get('category'),
      value: Number(formData.get('value')),
      date: formData.get('date'),
      type: formData.get('type')
    };
    await setDoc(doc(db, `artifacts/${appId}/public/data/expenses`, newExpense.id), newExpense);
    setIsNewExpenseModalOpen(false);
    e.target.reset();
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    if(!authUser) return;
    const formData = new FormData(e.target);
    const name = formData.get('name');
    const permissions = formData.getAll('permissions');

    if (permissions.length === 0) {
      alert("Atenção: Você precisa selecionar pelo menos uma permissão de acesso (Aba) para esse membro da equipe.");
      return;
    }

    const colors = ['bg-[#ff4d00]', 'bg-emerald-500', 'bg-purple-500', 'bg-amber-500', 'bg-blue-500', 'bg-rose-500', 'bg-indigo-500'];
    const randomColor = colors[Math.floor(Math.random() * colors.length)];
    const newUser = {
      id: `u${Date.now()}`, name: name, role: formData.get('role'), email: formData.get('email'),
      avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=random&color=fff`, color: randomColor,
      permissions: permissions
    };
    await setDoc(doc(db, `artifacts/${appId}/public/data/users`, newUser.id), newUser);
    setIsNewUserModalOpen(false);
    e.target.reset();
  };

  const updateTaskStatus = async (taskId, newStatus) => {
    if(!authUser) return;
    await updateDoc(doc(db, `artifacts/${appId}/public/data/tasks`, taskId), { status: newStatus });
  };

  const deleteTask = async (taskId) => {
    if(!authUser) return;
    await deleteDoc(doc(db, `artifacts/${appId}/public/data/tasks`, taskId));
  };

  const deleteClient = async (clientId) => {
    if(!authUser) return;
    await deleteDoc(doc(db, `artifacts/${appId}/public/data/clients`, clientId));
    if (selectedClient?.id === clientId) setSelectedClient(null);
  };

  const deleteSale = async (saleId) => {
    if(!authUser) return;
    await deleteDoc(doc(db, `artifacts/${appId}/public/data/sales`, saleId));
  };

  const deleteExpense = async (expenseId) => {
    if(!authUser) return;
    await deleteDoc(doc(db, `artifacts/${appId}/public/data/expenses`, expenseId));
  };

  const deleteUser = async (userId) => {
    if(!authUser) return;
    await deleteDoc(doc(db, `artifacts/${appId}/public/data/users`, userId));
  };

  if (isInitAuth || (authUser && loadingDb)) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#0a0a0a]">
         <div className="flex flex-col items-center">
            <Loader2 size={40} className="text-[#ff4d00] animate-spin mb-4" />
            <h2 className="text-white font-bold text-xl tracking-wide">Mind <span className="text-[#ff4d00]">Assessoria</span></h2>
            <p className="text-slate-400 text-sm mt-2">{isInitAuth ? 'Verificando sessão...' : 'Carregando banco de dados...'}</p>
         </div>
      </div>
    );
  }

  if (isPublicNpsRoute) {
    return <NpsPublicForm clientId={publicClientId} db={db} appId={appId} auth={auth} />;
  }

  if (!authUser) {
    return <LoginScreen onLogin={handleLogin} error={authError} isLoading={authLoading} />;
  }

  const renderContent = () => {
    if (selectedClient) return <ClientDetail client={selectedClient} onBack={() => setSelectedClient(null)} tasks={tasks} users={users} onOpenNewTask={() => setIsNewTaskModalOpen(true)} />;
    switch (activeTab) {
      case 'dashboard': return <DashboardTab setTab={setActiveTab} clients={clients} tasks={tasks} sales={sales} expenses={expenses} goals={goals} onOpenNewClient={() => setIsNewClientModalOpen(true)} onEditGoal={openEditGoalModal} />;
      case 'clientes': return <ClientesTab clients={filteredClients} users={users} onSelectClient={setSelectedClient} onOpenNewClient={() => setIsNewClientModalOpen(true)} onDeleteClient={deleteClient} onEditClient={openEditClient} searchQuery={searchQuery} setSearchQuery={setSearchQuery} />;
      case 'tarefas': return <TarefasTab tasks={filteredTasks} clients={clients} users={users} onOpenNewTask={() => setIsNewTaskModalOpen(true)} onUpdateTaskStatus={updateTaskStatus} onDeleteTask={deleteTask} />;
      case 'vendas': return <VendasTab sales={sales} clients={clients} goals={goals} onOpenNewSale={() => setIsNewSaleModalOpen(true)} onDeleteSale={deleteSale} onEditGoal={openEditGoalModal} />;
      case 'custos': return <CustosTab expenses={expenses} onOpenNewExpense={() => setIsNewExpenseModalOpen(true)} onDeleteExpense={deleteExpense} />;
      case 'nps': return <NpsTab npsData={npsData} clients={clients} />;
      case 'equipe': return <EquipeTab users={users} clients={clients} tasks={tasks} onOpenNewUser={() => setIsNewUserModalOpen(true)} onDeleteUser={deleteUser} />;
      default: return <DashboardTab setTab={setActiveTab} clients={clients} tasks={tasks} sales={sales} expenses={expenses} goals={goals} onOpenNewClient={() => setIsNewClientModalOpen(true)} onEditGoal={openEditGoalModal} />;
    }
  };

  const menuItems = [
    { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { id: 'clientes', icon: Users, label: 'Clientes' },
    { id: 'tarefas', icon: CheckSquare, label: 'Tarefas' },
    { id: 'vendas', icon: DollarSign, label: 'Vendas' },
    { id: 'custos', icon: Wallet, label: 'Despesas' },
    { id: 'nps', icon: HeartHandshake, label: 'NPS / Satisfação' },
    { id: 'equipe', icon: Briefcase, label: 'Equipe' },
  ].filter(item => userPermissions.includes(item.id));

  return (
    <div className="flex h-screen bg-slate-50 font-sans overflow-hidden">
      
      {/* BARRA LATERAL */}
      <aside className={`bg-[#0a0a0a] text-slate-300 w-64 flex-shrink-0 flex flex-col transition-all duration-300 z-20 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full fixed h-full'}`}>
        <div className="h-16 flex items-center px-6 border-b border-[#1c1c1c]">
          <img 
            src="/logo.png" alt="Logo Mind" 
            className="w-8 h-8 rounded-lg mr-3 shadow-lg shadow-[#ff4d00]/20 object-cover"
            onError={(e) => { e.target.onerror = null; e.target.src = "https://ui-avatars.com/api/?name=M&background=ff4d00&color=fff&rounded=false&bold=true"; }}
          />
          <span className="text-white font-bold text-lg tracking-wide">Mind <span className="text-[#ff4d00]">Assessoria</span></span>
        </div>

        <nav className="flex-1 py-6 px-4 space-y-1">
          {menuItems.map(item => (
            <button
              key={item.id}
              onClick={() => { setActiveTab(item.id); setSelectedClient(null); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                (!selectedClient && activeTab === item.id) ? 'bg-[#ff4d00]/15 text-[#ff4d00]' : 'hover:bg-[#1c1c1c] hover:text-white'
              }`}
            >
              <item.icon size={18} className={(!selectedClient && activeTab === item.id) ? 'text-[#ff4d00]' : 'text-slate-400'} />
              {item.label}
            </button>
          ))}
        </nav>

        <div className="p-4 border-t border-[#1c1c1c] space-y-2">
          <button className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium hover:bg-[#1c1c1c] hover:text-white transition-all">
            <Settings size={18} className="text-slate-400" /> Configurações
          </button>
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-rose-500 hover:bg-rose-500/10 transition-all">
            <LogOut size={18} /> Sair
          </button>
        </div>
      </aside>

      {!sidebarOpen && <div className="fixed inset-0 bg-[#0a0a0a]/50 z-10 lg:hidden" onClick={() => setSidebarOpen(true)}></div>}

      {/* CONTEÚDO PRINCIPAL */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden relative">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 sm:px-8 flex-shrink-0 z-10">
          <div className="flex items-center gap-4">
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 -ml-2 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors">
              <Menu size={20} />
            </button>
            <div className="hidden md:flex items-center text-sm text-slate-500 font-medium">
              <span className="text-slate-800">{new Date().toLocaleDateString('pt-BR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
            </div>
          </div>

          <div className="flex items-center gap-3 sm:gap-5">
            <div className="relative hidden sm:block">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
              <input 
                type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Busca rápida..." 
                className="w-48 lg:w-64 pl-9 pr-4 py-2 bg-slate-50 border-none rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#ff4d00]/20 transition-all"
              />
            </div>
            
            <div className="relative">
              <button 
                onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
                className={`relative p-2 transition-colors ${isNotificationsOpen ? 'text-[#ff4d00]' : 'text-slate-400 hover:text-[#ff4d00]'}`}
              >
                <Bell size={20} />
                {notifications.filter(n => n.type !== 'empty').length > 0 && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-rose-500 rounded-full border-2 border-white"></span>
                )}
              </button>

              {isNotificationsOpen && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setIsNotificationsOpen(false)}></div>
                  <div className="absolute right-0 mt-3 w-80 bg-white rounded-xl shadow-xl border border-slate-100 z-50 overflow-hidden animate-fade-in">
                    <div className="p-3 border-b border-slate-100 bg-slate-50 flex justify-between items-center">
                      <h3 className="font-bold text-slate-800 text-sm">Notificações</h3>
                      <span className="text-xs font-medium bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full">
                        {notifications.filter(n => n.type !== 'empty').length} novas
                      </span>
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {notifications.map(notif => (
                        <div key={notif.id} className="p-4 border-b border-slate-50 hover:bg-slate-50 transition-colors flex gap-3 items-start">
                          <div className={`mt-0.5 p-2 rounded-full flex-shrink-0 ${notif.type === 'warning' ? 'bg-rose-100' : notif.type === 'success' ? 'bg-emerald-100' : notif.type === 'empty' ? 'bg-slate-100' : 'bg-blue-100'}`}>
                            {notif.icon}
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-slate-800">{notif.title}</p>
                            <p className="text-xs text-slate-500 mt-1 leading-snug">{notif.message}</p>
                            {notif.time && <p className="text-[10px] text-slate-400 mt-1.5 font-medium uppercase tracking-wider">{notif.time}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="p-2 border-t border-slate-100 text-center bg-slate-50">
                      <button onClick={() => setIsNotificationsOpen(false)} className="text-xs text-[#ff4d00] font-medium hover:underline p-1">Fechar painel</button>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="h-8 w-px bg-slate-200 mx-1"></div>
            <button className="flex items-center gap-2 hover:bg-slate-50 p-1 pr-3 rounded-full transition-colors border border-transparent hover:border-slate-200">
              <div className="w-8 h-8 rounded-full bg-[#ff4d00] text-white flex items-center justify-center font-bold text-sm">
                {authUser?.email?.charAt(0).toUpperCase() || 'M'}
              </div>
              <span className="text-sm font-medium text-slate-700 hidden md:block truncate max-w-[120px]">
                {authUser?.email || 'Admin'}
              </span>
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-auto p-4 sm:p-8 scroll-smooth">
          <div className="max-w-7xl mx-auto">{renderContent()}</div>
        </div>
      </main>

      <Modal isOpen={isEditGoalModalOpen} onClose={() => setIsEditGoalModalOpen(false)} title={goalTypeToEdit === 'revenue' ? "Editar Meta Geral de Receita" : "Editar Meta de Novas Vendas"}>
        <form onSubmit={handleUpdateGoal} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Novo Valor da Meta (R$)
            </label>
            <input 
              required 
              name="goalValue" 
              type="number" 
              min="1"
              defaultValue={goalTypeToEdit === 'revenue' ? goals.monthlyRevenue : goals.newSales} 
              className="w-full px-4 py-3 border border-slate-300 rounded-lg text-lg focus:outline-none focus:border-[#ff4d00] focus:ring-2 focus:ring-[#ff4d00]/20" 
              placeholder="Ex: 50000" 
            />
          </div>
          <div className="pt-4 border-t border-slate-100 flex justify-end gap-3 mt-6">
            <button type="button" onClick={() => setIsEditGoalModalOpen(false)} className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg transition-colors">Cancelar</button>
            <button type="submit" className="px-4 py-2 text-sm font-medium text-white bg-[#ff4d00] hover:bg-[#d63a00] rounded-lg transition-colors">Salvar Meta</button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={isNewClientModalOpen} onClose={() => setIsNewClientModalOpen(false)} title="Registrar Novo Cliente">
        <form onSubmit={handleAddClient} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Nome da Empresa</label><input required name="name" type="text" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-[#ff4d00] focus:ring-1 focus:ring-[#ff4d00]" placeholder="Ex: Acme Corp" /></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Nicho / Setor</label><input required name="niche" type="text" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-[#ff4d00] focus:ring-1 focus:ring-[#ff4d00]" placeholder="Ex: E-commerce" /></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Valor do Contrato (MRR)</label><input required name="mrr" type="number" min="0" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-[#ff4d00] focus:ring-1 focus:ring-[#ff4d00]" placeholder="R$ 0,00" /></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Data de Início</label><input required name="startDate" type="date" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-[#ff4d00] focus:ring-1 focus:ring-[#ff4d00]" /></div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Status</label>
              <select name="status" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-[#ff4d00] focus:ring-1 focus:ring-[#ff4d00] bg-white">
                <option value="ativo">Ativo</option><option value="pausado">Pausado</option><option value="cancelado">Cancelado</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Responsáveis na Agência</label>
              <div className="border border-slate-300 rounded-lg p-2 max-h-24 overflow-y-auto bg-white">
                {users.map(u => (
                  <label key={u.id} className="flex items-center gap-2 p-1 hover:bg-slate-50 rounded cursor-pointer text-sm">
                    <input type="checkbox" name="ownerIds" value={u.id} className="rounded text-[#ff4d00] focus:ring-[#ff4d00] cursor-pointer" />
                    <span className="text-slate-700">{u.name} <span className="text-xs text-slate-400">({u.role})</span></span>
                  </label>
                ))}
                {users.length === 0 && <span className="text-xs text-slate-400">Adicione membros na aba Equipe primeiro.</span>}
              </div>
            </div>
          </div>
          <div><label className="block text-xs font-medium text-slate-700 mb-1">Serviços Contratados (Separados por vírgula)</label><input required name="services" type="text" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-[#ff4d00] focus:ring-1 focus:ring-[#ff4d00]" placeholder="Ex: Tráfego, Social Media, Design" /></div>
          <div><label className="block text-xs font-medium text-slate-700 mb-1">Observações Internas</label><textarea name="notes" rows="3" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-[#ff4d00] focus:ring-1 focus:ring-[#ff4d00]" placeholder="Detalhes importantes sobre a conta..."></textarea></div>
          <div className="pt-4 border-t border-slate-100 flex justify-end gap-3 mt-6">
            <button type="button" onClick={() => setIsNewClientModalOpen(false)} className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg transition-colors">Cancelar</button>
            <button type="submit" className="px-4 py-2 text-sm font-medium text-white bg-[#ff4d00] hover:bg-[#d63a00] rounded-lg transition-colors">Salvar Cliente</button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={isEditClientModalOpen} onClose={() => {setIsEditClientModalOpen(false); setEditingClient(null);}} title="Editar Cliente">
        {editingClient && (
          <form onSubmit={handleUpdateClient} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><label className="block text-xs font-medium text-slate-700 mb-1">Nome da Empresa</label><input required name="name" defaultValue={editingClient.name} type="text" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-[#ff4d00] focus:ring-1 focus:ring-[#ff4d00]" /></div>
              <div><label className="block text-xs font-medium text-slate-700 mb-1">Nicho / Setor</label><input required name="niche" defaultValue={editingClient.niche} type="text" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-[#ff4d00] focus:ring-1 focus:ring-[#ff4d00]" /></div>
              <div><label className="block text-xs font-medium text-slate-700 mb-1">Valor do Contrato (MRR)</label><input required name="mrr" defaultValue={editingClient.mrr} type="number" min="0" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-[#ff4d00] focus:ring-1 focus:ring-[#ff4d00]" /></div>
              <div><label className="block text-xs font-medium text-slate-700 mb-1">Data de Início</label><input required name="startDate" defaultValue={editingClient.startDate} type="date" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-[#ff4d00] focus:ring-1 focus:ring-[#ff4d00]" /></div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Status</label>
                <select name="status" defaultValue={editingClient.status} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-[#ff4d00] focus:ring-1 focus:ring-[#ff4d00] bg-white">
                  <option value="ativo">Ativo</option><option value="pausado">Pausado</option><option value="cancelado">Cancelado</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Responsáveis na Agência</label>
                <div className="border border-slate-300 rounded-lg p-2 max-h-24 overflow-y-auto bg-white">
                  {users.map(u => {
                    const isChecked = (editingClient.ownerIds || [editingClient.ownerId]).includes(u.id);
                    return (
                      <label key={u.id} className="flex items-center gap-2 p-1 hover:bg-slate-50 rounded cursor-pointer text-sm">
                        <input type="checkbox" name="ownerIds" value={u.id} defaultChecked={isChecked} className="rounded text-[#ff4d00] focus:ring-[#ff4d00] cursor-pointer" />
                        <span className="text-slate-700">{u.name} <span className="text-xs text-slate-400">({u.role})</span></span>
                      </label>
                    );
                  })}
                  {users.length === 0 && <span className="text-xs text-slate-400">Nenhum membro cadastrado.</span>}
                </div>
              </div>
            </div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Serviços Contratados (Separados por vírgula)</label><input required name="services" defaultValue={editingClient.services?.join(', ')} type="text" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-[#ff4d00] focus:ring-1 focus:ring-[#ff4d00]" /></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Observações Internas</label><textarea name="notes" defaultValue={editingClient.notes} rows="3" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-[#ff4d00] focus:ring-1 focus:ring-[#ff4d00]"></textarea></div>
            <div className="pt-4 border-t border-slate-100 flex justify-end gap-3 mt-6">
              <button type="button" onClick={() => {setIsEditClientModalOpen(false); setEditingClient(null);}} className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg transition-colors">Cancelar</button>
              <button type="submit" className="px-4 py-2 text-sm font-medium text-white bg-[#ff4d00] hover:bg-[#d63a00] rounded-lg transition-colors">Salvar Alterações</button>
            </div>
          </form>
        )}
      </Modal>

      <Modal isOpen={isNewExpenseModalOpen} onClose={() => setIsNewExpenseModalOpen(false)} title="Registrar Nova Despesa">
        <form onSubmit={handleAddExpense} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Descrição</label>
            <input required name="description" type="text" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-[#ff4d00] focus:ring-1 focus:ring-[#ff4d00]" placeholder="Ex: Assinatura do Canva, Salário do João..." />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Valor (R$)</label><input required name="value" type="number" min="0" step="0.01" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-[#ff4d00] focus:ring-1 focus:ring-[#ff4d00]" placeholder="0,00" /></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Data da Despesa</label><input required name="date" type="date" defaultValue={new Date().toISOString().split('T')[0]} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-[#ff4d00] focus:ring-1 focus:ring-[#ff4d00]" /></div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Categoria</label>
              <select name="category" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-[#ff4d00] focus:ring-1 focus:ring-[#ff4d00] bg-white">
                <option value="Folha de Pagamento">Folha de Pagamento</option>
                <option value="Ferramentas e Softwares">Ferramentas / Softwares</option>
                <option value="Tráfego e Anúncios">Tráfego / Anúncios</option>
                <option value="Impostos e Taxas">Impostos e Taxas</option>
                <option value="Outros">Outros</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Tipo do Custo</label>
              <select name="type" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-[#ff4d00] focus:ring-1 focus:ring-[#ff4d00] bg-white">
                <option value="fixo">Custo Fixo (Recorrente)</option>
                <option value="variavel">Custo Variável (Pontual)</option>
              </select>
            </div>
          </div>
          <div className="pt-4 border-t border-slate-100 flex justify-end gap-3 mt-6">
            <button type="button" onClick={() => setIsNewExpenseModalOpen(false)} className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg transition-colors">Cancelar</button>
            <button type="submit" className="px-4 py-2 text-sm font-medium text-white bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors">Salvar Despesa</button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={isNewTaskModalOpen} onClose={() => setIsNewTaskModalOpen(false)} title="Criar Nova Tarefa">
        <form onSubmit={handleAddTask} className="space-y-4">
          <div><label className="block text-xs font-medium text-slate-700 mb-1">Título da Tarefa</label><input required name="title" type="text" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-[#ff4d00] focus:ring-1 focus:ring-[#ff4d00]" placeholder="Ex: Criar campanha para Black Friday" /></div>
          
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Descrição / Links / Imagens</label>
            <textarea name="description" rows="3" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-[#ff4d00] focus:ring-1 focus:ring-[#ff4d00]" placeholder="Cole aqui os links de referência, do Drive, Canva, link de imagem ou escreva os detalhes da tarefa..."></textarea>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Cliente Vinculado</label>
              <select required name="clientId" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-[#ff4d00] focus:ring-1 focus:ring-[#ff4d00] bg-white">
                <option value="">Selecione um cliente...</option>
                {clients.map(c => (<option key={c.id} value={c.id}>{c.name}</option>))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Responsável</label>
              <select required name="ownerId" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-[#ff4d00] focus:ring-1 focus:ring-[#ff4d00] bg-white">
                {users.map(u => (<option key={u.id} value={u.id}>{u.name} ({u.role})</option>))}
              </select>
            </div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Prazo (Deadline)</label><input required name="deadline" type="date" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-[#ff4d00] focus:ring-1 focus:ring-[#ff4d00]" /></div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Prioridade</label>
              <select name="priority" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-[#ff4d00] focus:ring-1 focus:ring-[#ff4d00] bg-white">
                <option value="baixa">Baixa</option><option value="media">Média</option><option value="alta">Alta</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Status</label>
              <select name="status" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-[#ff4d00] focus:ring-1 focus:ring-[#ff4d00] bg-white">
                <option value="pendente">Pendente</option><option value="em_andamento">Em Andamento</option><option value="concluida">Concluída</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Tipo de Tarefa</label>
              <select name="type" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-[#ff4d00] focus:ring-1 focus:ring-[#ff4d00] bg-white">
                <option value="campanha">Campanha</option><option value="criativo">Criativo / Design</option><option value="copy">Copywriting</option>
                <option value="relatorio">Relatório</option><option value="reuniao">Reunião</option><option value="operacional">Operacional</option>
              </select>
            </div>
          </div>
          <div className="pt-4 border-t border-slate-100 flex justify-end gap-3 mt-6">
            <button type="button" onClick={() => setIsNewTaskModalOpen(false)} className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg transition-colors">Cancelar</button>
            <button type="submit" className="px-4 py-2 text-sm font-medium text-white bg-[#ff4d00] hover:bg-[#d63a00] rounded-lg transition-colors">Criar Tarefa</button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={isNewSaleModalOpen} onClose={() => { setIsNewSaleModalOpen(false); setIsAddingNewClientInSale(false); }} title="Registrar Nova Movimentação">
        <form onSubmit={handleAddSale} className="space-y-4">
          
          <div className="flex bg-slate-100 p-1 rounded-lg w-full mb-2">
            <label className={`flex-1 text-center py-2 text-sm font-medium rounded-md cursor-pointer transition-colors ${!isAddingNewClientInSale ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500 hover:text-slate-700'}`}>
              <input type="radio" name="isNewClient" value="false" checked={!isAddingNewClientInSale} onChange={() => setIsAddingNewClientInSale(false)} className="hidden" />
              Cliente Existente
            </label>
            <label className={`flex-1 text-center py-2 text-sm font-medium rounded-md cursor-pointer transition-colors ${isAddingNewClientInSale ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500 hover:text-slate-700'}`}>
              <input type="radio" name="isNewClient" value="true" checked={isAddingNewClientInSale} onChange={() => setIsAddingNewClientInSale(true)} className="hidden" />
              Novo Cliente
            </label>
          </div>

          {!isAddingNewClientInSale ? (
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Selecione o Cliente</label>
              <select required={!isAddingNewClientInSale} name="clientId" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-[#ff4d00] focus:ring-1 focus:ring-[#ff4d00] bg-white">
                <option value="">Selecione o cliente...</option>
                {clients.map(c => (<option key={c.id} value={c.id}>{c.name}</option>))}
              </select>
            </div>
          ) : (
            <div className="p-4 bg-[#ff4d00]/5 border border-[#ff4d00]/20 rounded-lg space-y-3">
               <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Nome da Empresa</label>
                    <input required={isAddingNewClientInSale} name="newClientName" type="text" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-[#ff4d00] focus:ring-1 focus:ring-[#ff4d00]" placeholder="Ex: Acme Corp" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Nicho / Setor</label>
                    <input name="newClientNiche" type="text" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-[#ff4d00] focus:ring-1 focus:ring-[#ff4d00]" placeholder="Ex: E-commerce" />
                  </div>
               </div>
               <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Responsáveis na Agência (Opcional)</label>
                  <div className="border border-slate-300 rounded-lg p-2 max-h-24 overflow-y-auto bg-white">
                    {users.map(u => (
                      <label key={u.id} className="flex items-center gap-2 p-1 hover:bg-slate-50 rounded cursor-pointer text-sm">
                        <input type="checkbox" name="ownerIds" value={u.id} className="rounded text-[#ff4d00] focus:ring-[#ff4d00] cursor-pointer" />
                        <span className="text-slate-700">{u.name} <span className="text-xs text-slate-400">({u.role})</span></span>
                      </label>
                    ))}
                    {users.length === 0 && <span className="text-xs text-slate-400">Nenhum membro cadastrado.</span>}
                  </div>
               </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">{isAddingNewClientInSale ? 'Valor do Contrato (MRR)' : 'Valor da Movimentação (R$)'}</label>
              <input required name="value" type="number" min="0" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-[#ff4d00] focus:ring-1 focus:ring-[#ff4d00]" placeholder="0,00" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Data</label>
              <input required name="date" type="date" defaultValue={new Date().toISOString().split('T')[0]} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-[#ff4d00] focus:ring-1 focus:ring-[#ff4d00]" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-slate-700 mb-1">Tipo de Movimentação</label>
              <select name="type" disabled={isAddingNewClientInSale} className={`w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-[#ff4d00] focus:ring-1 focus:ring-[#ff4d00] ${isAddingNewClientInSale ? 'bg-slate-100 text-slate-500 cursor-not-allowed' : 'bg-white'}`}>
                <option value="novo_contrato">Novo Contrato (MRR Novo)</option>
                <option value="upsell">Upsell (Aumento de Contrato Atual)</option>
                <option value="projeto_pontual">Projeto Pontual (Taxa Única)</option>
                <option value="cancelamento">Cancelamento / Downgrade (Churn)</option>
              </select>
              {isAddingNewClientInSale && <p className="text-[10px] text-slate-500 mt-1">Ao cadastrar um novo cliente, a movimentação será registrada automaticamente como "Novo Contrato" e o valor acima definirá o MRR inicial dele.</p>}
            </div>
          </div>
          <div className="pt-4 border-t border-slate-100 flex justify-end gap-3 mt-6">
            <button type="button" onClick={() => { setIsNewSaleModalOpen(false); setIsAddingNewClientInSale(false); }} className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg transition-colors">Cancelar</button>
            <button type="submit" className="px-4 py-2 text-sm font-medium text-white bg-[#ff4d00] hover:bg-[#d63a00] rounded-lg transition-colors">Registrar Movimentação</button>
          </div>
        </form>
      </Modal>

      {/* MODAL ADICIONAR MEMBRO COM PERMISSÕES */}
      <Modal isOpen={isNewUserModalOpen} onClose={() => setIsNewUserModalOpen(false)} title="Adicionar Membro à Equipe">
        <form onSubmit={handleAddUser} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Nome Completo</label>
            <input required name="name" type="text" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-[#ff4d00] focus:ring-1 focus:ring-[#ff4d00]" placeholder="Ex: João Silva" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Cargo / Função</label>
              <input required name="role" type="text" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-[#ff4d00] focus:ring-1 focus:ring-[#ff4d00]" placeholder="Ex: Gestor de Tráfego" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">E-mail de Acesso</label>
              <input required name="email" type="email" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-[#ff4d00] focus:ring-1 focus:ring-[#ff4d00]" placeholder="joao@mindassessoria.com" />
            </div>
          </div>
          
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Permissões de Acesso (Abas que pode ver)</label>
            <div className="grid grid-cols-2 gap-2 border border-slate-300 rounded-lg p-3 bg-slate-50">
              {[
                { id: 'dashboard', label: 'Dashboard' },
                { id: 'clientes', label: 'Clientes' },
                { id: 'tarefas', label: 'Tarefas' },
                { id: 'vendas', label: 'Vendas' },
                { id: 'custos', label: 'Despesas / Custos' },
                { id: 'nps', label: 'NPS / Satisfação' },
                { id: 'equipe', label: 'Equipe' },
              ].map(perm => (
                <label key={perm.id} className="flex items-center gap-2 cursor-pointer text-sm hover:bg-slate-100 p-1 rounded transition-colors">
                  <input type="checkbox" name="permissions" value={perm.id} defaultChecked className="rounded text-[#ff4d00] focus:ring-[#ff4d00]" />
                  <span className="text-slate-700">{perm.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg mt-4 flex items-start gap-2">
            <AlertCircle size={16} className="text-blue-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-blue-800">
              Lembre-se: Para que este membro consiga fazer login no sistema, você precisa adicionar este e-mail na aba "Authentication" do Firebase e criar uma senha para ele.
            </p>
          </div>
          <div className="pt-4 border-t border-slate-100 flex justify-end gap-3 mt-6">
            <button type="button" onClick={() => setIsNewUserModalOpen(false)} className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg transition-colors">Cancelar</button>
            <button type="submit" className="px-4 py-2 text-sm font-medium text-white bg-[#ff4d00] hover:bg-[#d63a00] rounded-lg transition-colors">Adicionar Membro</button>
          </div>
        </form>
      </Modal>

      <style dangerouslySetInnerHTML={{__html: `
        .animate-fade-in { animation: fadeIn 0.3s ease-out; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        ::-webkit-scrollbar { width: 8px; height: 8px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
        ::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
      `}} />
    </div>
  );
}